import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle2, Clock, ChefHat, Truck, Package, 
  MapPin, Phone, AlertCircle, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  PENDENTE: { 
    icon: Clock, 
    color: 'bg-yellow-100 text-yellow-700', 
    step: 1,
    label: 'Aguardando confirmação'
  },
  CONFIRMADO: { 
    icon: CheckCircle2, 
    color: 'bg-blue-100 text-blue-700', 
    step: 2,
    label: 'Pedido confirmado'
  },
  PREPARO: { 
    icon: ChefHat, 
    color: 'bg-orange-100 text-orange-700', 
    step: 3,
    label: 'Em preparo'
  },
  PRONTO: { 
    icon: Package, 
    color: 'bg-emerald-100 text-emerald-700', 
    step: 3,
    label: 'Pronto'
  },
  SAIU: { 
    icon: Truck, 
    color: 'bg-purple-100 text-purple-700', 
    step: 4,
    label: 'Saiu para entrega'
  },
  ENTREGUE: { 
    icon: Package, 
    color: 'bg-green-100 text-green-700', 
    step: 5,
    label: 'Entregue'
  },
  CANCELADO: { 
    icon: AlertCircle, 
    color: 'bg-red-100 text-red-700', 
    step: 0,
    label: 'Cancelado'
  }
};

export default function OrderTrackingPage() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchOrder();
    
    // Auto refresh every 30 seconds
    const interval = setInterval(fetchOrder, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const fetchOrder = async () => {
    try {
      setRefreshing(true);
      const response = await fetch(`${API_URL}/api/public/orders/track/${token}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Pedido não encontrado');
        }
        if (response.status === 410) {
          throw new Error('Link de acompanhamento expirado');
        }
        throw new Error('Erro ao carregar pedido');
      }
      
      const data = await response.json();
      setOrder(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando pedido...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Ops!</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStatus = statusConfig[order.status] || statusConfig.PENDENTE;
  const StatusIcon = currentStatus.icon;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#FF6B6B] to-[#FF7A5A] text-white py-6">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold">Acompanhe seu Pedido</h1>
          <p className="text-orange-100 mt-1">#{order.display_id}</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Status Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${currentStatus.color} mb-4`}>
                <StatusIcon className="w-10 h-10" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">{order.status_label}</h2>
              <p className="text-slate-500 text-sm mt-1">
                Pedido realizado em {formatDate(order.created_at)}
              </p>
            </div>

            {/* Progress Steps */}
            {order.status !== 'CANCELADO' && (
              <div className="relative">
                <div className="flex justify-between items-center">
                  {['PENDENTE', 'CONFIRMADO', 'PREPARO', 'SAIU', 'ENTREGUE'].map((status, index) => {
                    const config = statusConfig[status];
                    const isActive = currentStatus.step >= config.step;
                    const Icon = config.icon;
                    
                    return (
                      <div key={status} className="flex flex-col items-center relative z-10">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isActive ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-400'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <span className={`text-xs mt-2 text-center ${
                          isActive ? 'text-green-600 font-medium' : 'text-slate-400'
                        }`}>
                          {config.label.split(' ')[0]}
                        </span>
                      </div>
                    );
                  })}
                </div>
                
                {/* Progress Line */}
                <div className="absolute top-5 left-5 right-5 h-0.5 bg-slate-200 -z-0">
                  <div 
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${Math.min(100, (currentStatus.step - 1) * 25)}%` }}
                  />
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchOrder}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Order Items */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="font-semibold text-slate-800 mb-4">Itens do Pedido</h3>
            
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="w-8 h-8 flex items-center justify-center rounded-full">
                      {item.quantity}x
                    </Badge>
                    <span className="text-slate-700">{item.name}</span>
                  </div>
                  <span className="font-medium">{formatCurrency(item.total)}</span>
                </div>
              ))}
            </div>
            
            <Separator className="my-4" />
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span>{formatCurrency(order.subtotal)}</span>
              </div>
              
              {order.discount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Desconto</span>
                  <span>-{formatCurrency(order.discount)}</span>
                </div>
              )}
              
              {order.delivery_fee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Taxa de entrega</span>
                  <span>{formatCurrency(order.delivery_fee)}</span>
                </div>
              )}
              
              <Separator className="my-2" />
              
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-[#FF6B6B]">{formatCurrency(order.total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Status */}
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Status do Pagamento</span>
              <Badge variant={order.payment_status === 'PAGO' || order.payment_status === 'PAID' || order.payment_status === 'APPROVED' ? 'default' : 'secondary'}>
                {order.payment_status === 'PAGO' || order.payment_status === 'PAID' || order.payment_status === 'APPROVED' ? 'Pago' : 'Pendente'}
              </Badge>
            </div>
            {order.payment_mode && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Forma</span>
                <span className="text-slate-700 font-medium">
                  {order.payment_mode === 'ONLINE' ? 'Pago Online' :
                   order.payment_mode === 'COD' ? 'Na Entrega' :
                   order.payment_mode === 'POS_STORE' ? 'No Balcao' :
                   order.payment_mode}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help */}
        <div className="text-center text-sm text-slate-500">
          <p>Dúvidas sobre seu pedido?</p>
          <p className="flex items-center justify-center gap-1 mt-1">
            <Phone className="w-4 h-4" />
            Entre em contato com o restaurante
          </p>
        </div>
      </main>
    </div>
  );
}
